/**
 * Account deletion flow helpers (kept pure so they can be unit tested).
 *
 * The backend deletes the account in one transaction: an error response means
 * the account and its data were not deleted, so the error copy says so. With
 * no response at all (network error, timeout) the server may still have
 * committed, so that case gets "outcome unknown" copy instead. Server `detail`
 * strings are English-only and never shown here; every message is a
 * translation key.
 */

export type DeleteAccountErrorKey =
  | 'profile.deleteAccountBillingFailed'
  | 'profile.deleteAccountRateLimited'
  | 'profile.deleteAccountFailedNotDeleted'
  | 'profile.deleteAccountOutcomeUnknown';

type HttpErrorLike = { response?: { status?: number } } | null | undefined;

function statusOf(error: unknown): number | undefined {
  return (error as HttpErrorLike)?.response?.status;
}

/** Translation key for a failed deletion request. */
export function deleteAccountErrorKey(error: unknown): DeleteAccountErrorKey {
  const status = statusOf(error);
  if (status === 502) return 'profile.deleteAccountBillingFailed';
  if (status === 429) return 'profile.deleteAccountRateLimited';
  if (!status) return 'profile.deleteAccountOutcomeUnknown';
  return 'profile.deleteAccountFailedNotDeleted';
}

/** Analytics-safe error classification (no messages, no PII). */
export function deleteAccountErrorType(error: unknown): string {
  const status = statusOf(error);
  if (status) return `http_${status}`;
  return (error as { message?: string } | null)?.message ? 'exception' : 'unknown';
}

export type CompleteAccountDeletionDeps = {
  closeDeleteModal: () => void;
  notifyDeleted: () => void;
  logout: () => Promise<void>;
};

/**
 * After a successful deletion the session is dead: close the modal, tell the
 * user, and log out directly. There is deliberately no confirmation step and
 * no way to cancel (ADM-15).
 */
export async function completeAccountDeletion(deps: CompleteAccountDeletionDeps): Promise<void> {
  deps.closeDeleteModal();
  deps.notifyDeleted();
  await deps.logout();
}
